import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useQBImport, IMPORT_ENTITIES, type ImportEntityType, type ImportResult } from "@/hooks/useQBImport";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, ArrowRight, RotateCcw } from "lucide-react";
import { ImportResultRow } from "./ImportResultRow";

type WizardStep = "select" | "confirm" | "progress" | "results";

interface EntityStatus {
  entity: ImportEntityType;
  status: "pending" | "running" | "done" | "error";
  result?: ImportResult;
  error?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  companyId: string;
}

export function QBImportWizard({ open, onOpenChange, companyId }: Props) {
  const { runEntityImport } = useQBImport();
  const [step, setStep] = useState<WizardStep>("select");
  const [selected, setSelected] = useState<Set<ImportEntityType>>(new Set());
  const [entityStatuses, setEntityStatuses] = useState<EntityStatus[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const reset = () => {
    setStep("select");
    setSelected(new Set());
    setEntityStatuses([]);
    setIsRunning(false);
  };

  const handleClose = () => {
    if (isRunning) return; // Prevent closing during import
    reset();
    onOpenChange(false);
  };

  const toggleEntity = (key: ImportEntityType) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(IMPORT_ENTITIES.map((e) => e.key)));
  const deselectAll = () => setSelected(new Set());

  // Check for missing dependencies
  const missingDeps = IMPORT_ENTITIES
    .filter((e) => selected.has(e.key) && e.dependsOn)
    .flatMap((e) => (e.dependsOn ?? []).filter((dep) => !selected.has(dep)))
    .filter((v, i, a) => a.indexOf(v) === i); // dedupe

  // Ordered list of selected entities (maintaining dependency order)
  const orderedSelected = IMPORT_ENTITIES.filter((e) => selected.has(e.key));

  const startImport = useCallback(async () => {
    setIsRunning(true);
    const statuses: EntityStatus[] = orderedSelected.map((e) => ({
      entity: e.key,
      status: "pending",
    }));
    setEntityStatuses([...statuses]);
    setStep("progress");

    for (let i = 0; i < statuses.length; i++) {
      statuses[i] = { ...statuses[i], status: "running" };
      setEntityStatuses([...statuses]);

      try {
        const result = await runEntityImport(companyId, statuses[i].entity);
        statuses[i] = { ...statuses[i], status: "done", result };
      } catch (err: any) {
        statuses[i] = { ...statuses[i], status: "error", error: err.message };
      }
      setEntityStatuses([...statuses]);
    }

    setIsRunning(false);
    setStep("results");
  }, [orderedSelected, companyId, runEntityImport]);

  const completedCount = entityStatuses.filter((s) => s.status === "done").length;
  const errorCount = entityStatuses.filter((s) => s.status === "error").length;
  const progressPct = entityStatuses.length > 0
    ? ((completedCount + errorCount) / entityStatuses.length) * 100
    : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === "select" && "QuickBooks Import"}
            {step === "confirm" && "Confirm Import"}
            {step === "progress" && "Importing..."}
            {step === "results" && "Import Complete"}
          </DialogTitle>
          <DialogDescription>
            {step === "select" && "Select which data to import from QuickBooks."}
            {step === "confirm" && "Review your selections before starting."}
            {step === "progress" && "Please wait while data is imported."}
            {step === "results" && "Import finished. Review the results below."}
          </DialogDescription>
        </DialogHeader>

        {/* ── Step 1: Select Entities ──────────────────────────── */}
        {step === "select" && (
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>Clear</Button>
            </div>

            <div className="space-y-2">
              {IMPORT_ENTITIES.map((entity) => (
                <label
                  key={entity.key}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected.has(entity.key) ? "border-primary bg-primary/5" : "hover:bg-accent/50"}`}
                >
                  <Checkbox
                    checked={selected.has(entity.key)}
                    onCheckedChange={() => toggleEntity(entity.key)}
                  />
                  <span className="text-lg">{entity.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{entity.label}</p>
                    <p className="text-xs text-muted-foreground">{entity.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Confirm ──────────────────────────────────── */}
        {step === "confirm" && (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              {orderedSelected.map((entity, i) => (
                <div key={entity.key} className="flex items-center gap-3 p-2 rounded-md bg-muted/30">
                  <span className="text-sm font-mono text-muted-foreground w-5">{i + 1}.</span>
                  <span className="text-lg">{entity.icon}</span>
                  <span className="text-sm font-medium">{entity.label}</span>
                </div>
              ))}
            </div>

            {missingDeps.length > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-300">Missing dependencies</p>
                  <p className="text-amber-700 dark:text-amber-400">
                    Consider also importing: {missingDeps.map((d) => IMPORT_ENTITIES.find((e) => e.key === d)?.label).join(", ")}
                  </p>
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              {orderedSelected.length} entity type{orderedSelected.length !== 1 ? "s" : ""} will be imported sequentially.
              Existing records will be updated (not duplicated).
            </p>
          </div>
        )}

        {/* ── Step 3: Progress ─────────────────────────────────── */}
        {step === "progress" && (
          <div className="space-y-4 py-2">
            <Progress value={progressPct} className="h-2" />
            <p className="text-xs text-muted-foreground text-center">
              {completedCount + errorCount} of {entityStatuses.length} complete
            </p>

            <div className="space-y-2">
              {entityStatuses.map((es) => {
                const entity = IMPORT_ENTITIES.find((e) => e.key === es.entity)!;
                return (
                  <div key={es.entity} className="flex items-center gap-3 p-2 rounded-md border">
                    {es.status === "pending" && <span className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />}
                    {es.status === "running" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                    {es.status === "done" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    {es.status === "error" && <XCircle className="h-4 w-4 text-destructive" />}
                    <span className="text-lg">{entity.icon}</span>
                    <span className="text-sm font-medium flex-1">{entity.label}</span>
                    {es.status === "done" && es.result && (
                      <Badge variant="secondary" className="text-[10px]">
                        {es.result[es.entity] ?? 0} imported
                      </Badge>
                    )}
                    {es.status === "error" && (
                      <Badge variant="destructive" className="text-[10px]">Failed</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Step 4: Results ──────────────────────────────────── */}
        {step === "results" && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded-md p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">{completedCount}</p>
                <p className="text-xs text-muted-foreground">Succeeded</p>
              </div>
              {errorCount > 0 && (
                <div className="border rounded-md p-3 text-center">
                  <p className="text-2xl font-bold text-destructive">{errorCount}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {entityStatuses.map((es) => (
                <ImportResultRow key={es.entity} es={es} companyId={companyId} />
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "select" && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={() => setStep("confirm")} disabled={selected.size === 0}>
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </>
          )}
          {step === "confirm" && (
            <>
              <Button variant="outline" onClick={() => setStep("select")}>Back</Button>
              <Button onClick={startImport}>
                Start Import ({orderedSelected.length} type{orderedSelected.length !== 1 ? "s" : ""})
              </Button>
            </>
          )}
          {step === "progress" && (
            <p className="text-xs text-muted-foreground">Import in progress — please don't close this window.</p>
          )}
          {step === "results" && (
            <>
              {errorCount > 0 && (
                <Button variant="outline" onClick={() => { setStep("select"); setEntityStatuses([]); }}>
                  <RotateCcw className="mr-1 h-4 w-4" /> Retry
                </Button>
              )}
              <Button onClick={handleClose}>Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
